<script lang="ts">
  import { onMount } from 'svelte'
  import { fade, slide } from 'svelte/transition'
  import { api } from '../../lib/api'
  import Modal from '../../components/ui/Modal.svelte'
  import ConfirmModal from '../../components/ui/ConfirmModal.svelte'
  import UserForm from '../../components/forms/UserForm.svelte'
  import { addToast } from '../../lib/toast'
  import { validateEmail } from '../../lib/validator'

  type User = { 
    id: string; 
    email: string; 
    user_type_id: string; 
    active: boolean;
    created_at?: string;
    api_token_count?: number;
  }

  let users: User[] = []
  let loading = false
  let error = ''
  
  // Paginación y filtro
  let currentPage = 1
  let pageSize = 10
  let totalPages = 1
  let total = 0
  let searchQuery = ''
  let searchTimeout: ReturnType<typeof setTimeout>
  
  // Modal State
  let showModal = false
  let modalMode: 'create' | 'edit' = 'create'
  let editingId = ''
  
  // Confirm Modal State
  let showConfirm = false
  let confirmId = ''
  
  // API Token Modal State
  let showAPITokenModal = false
  let apiTokenLoading = false
  let apiTokenData: { 
    token?: string; 
    user_id?: string; 
    name?: string;
    id?: string;
    exists?: boolean;
    created_at?: string;
  } | null = null
  let currentUserIdForToken = ''

  // Form Data
  let formData = {
    email: '',
    password: '',
    user_type_id: 'client'
  }
  let formLoading = false

  async function loadUsers() {
    loading = true
    try {
      const params = new URLSearchParams({
        page: currentPage.toString(),
        pageSize: pageSize.toString()
      })
      if (searchQuery) {
        params.append('search', searchQuery)
      }
      
      const res = await api.get(`/api/v1/admin/users/clients?${params.toString()}`)
      users = res.users || []
      total = res.pagination?.total || 0
      totalPages = res.pagination?.totalPages || 1
    } catch (e: any) {
      error = e.message
      addToast('Error al cargar usuarios: ' + e.message, 'error')
      users = []
    } finally {
      loading = false
    }
  }
  
  function handleSearch() {
    clearTimeout(searchTimeout)
    searchTimeout = setTimeout(() => {
      currentPage = 1
      loadUsers()
    }, 500) // Debounce de 500ms
  }
  
  function goToPage(page: number) {
    if (page >= 1 && page <= totalPages) {
      currentPage = page
      loadUsers()
    }
  }

  function openCreateModal() {
    modalMode = 'create'
    formData = { email: '', password: '', user_type_id: 'client' }
    showModal = true
  }

  function openEditModal(u: User) {
    modalMode = 'edit'
    editingId = u.id
    formData = { 
      email: u.email, 
      password: '', 
      user_type_id: u.user_type_id 
    }
    showModal = true
  }

  async function handleSubmit() {
    // Validar email antes de enviar
    if (modalMode === 'create' || formData.email) {
      if (!formData.email || !formData.email.trim()) {
        addToast('El email es requerido', 'error')
        return
      }
      if (!validateEmail(formData.email)) {
        addToast('El email no tiene un formato válido', 'error')
        return
      }
    }

    // Validar contraseña en creación
    if (modalMode === 'create' && (!formData.password || formData.password.trim().length < 8)) {
      addToast('La contraseña debe tener al menos 8 caracteres', 'error')
      return
    }

    formLoading = true
    try {
      if (modalMode === 'create') {
        const data = { ...formData, user_type_id: 'client' }
        const response = await api.post('/api/v1/admin/users', data)
        addToast(response.message || 'Usuario creado exitosamente', 'success')
      } else {
        const updateData: any = { email: formData.email }
        if (formData.password) updateData.password = formData.password
        
        const response = await api.put(`/api/v1/admin/users/${editingId}`, updateData)
        addToast(response.message || 'Usuario actualizado exitosamente', 'success')
      }
      showModal = false
      currentPage = 1
      loadUsers()
    } catch (e: any) {
      addToast(e.message, 'error')
    } finally {
      formLoading = false
    }
  }

  async function toggleActive(u: User) {
    try {
      await api.put(`/api/v1/admin/users/${u.id}/active`, { active: !u.active })
      u.active = !u.active
      users = [...users]
      addToast(`Usuario ${u.active ? 'activado' : 'desactivado'} correctamente`, 'success')
    } catch (e: any) {
      addToast(e.message, 'error')
      loadUsers()
    }
  }

  function confirmDelete(id: string) {
    confirmId = id
    showConfirm = true
  }

  async function deleteUser() {
    try {
      await api.delete(`/api/v1/admin/users/${confirmId}`)
      users = users.filter(u => u.id !== confirmId)
      showConfirm = false
      addToast('Usuario eliminado correctamente', 'success')
    } catch (e: any) {
      addToast(e.message, 'error')
    }
  }
  
  async function openAPITokenModal(userId: string) {
    currentUserIdForToken = userId
    apiTokenLoading = true
    showAPITokenModal = true
    try {
      // Primero intentar obtener el token actual
      const res = await api.get(`/api/v1/admin/users/${userId}/api-token`)
      apiTokenData = res
      if (!res.exists || !res.token) {
        // Si no existe, crear uno nuevo
        await createNewAPIToken(userId)
      }
    } catch (e: any) {
      // Si el endpoint no existe o hay error, intentar crear uno nuevo
      if (e.message.includes('404') || e.message.includes('not found')) {
        await createNewAPIToken(userId)
      } else {
        addToast('Error al obtener token API: ' + e.message, 'error')
        showAPITokenModal = false
      }
    } finally {
      apiTokenLoading = false
    }
  }

  async function createNewAPIToken(userId: string) {
    apiTokenLoading = true
    try {
      const res = await api.post(`/api/v1/admin/users/${userId}/api-token`)
      apiTokenData = res
      addToast('Token API creado exitosamente', 'success')
      loadUsers()
    } catch (e: any) {
      addToast('Error al crear token API: ' + e.message, 'error')
      showAPITokenModal = false
    } finally {
      apiTokenLoading = false
    }
  }

  async function regenerateAPIToken() {
    if (!currentUserIdForToken) return
    apiTokenLoading = true
    try {
      const res = await api.post(`/api/v1/admin/users/${currentUserIdForToken}/api-token`)
      apiTokenData = res
      addToast('Token API regenerado exitosamente', 'success')
      loadUsers()
    } catch (e: any) {
      addToast('Error al regenerar token API: ' + e.message, 'error')
    } finally {
      apiTokenLoading = false
    }
  }
  
  function copyAPIToken() {
    if (apiTokenData?.token) {
      navigator.clipboard.writeText(apiTokenData.token)
      addToast('Token copiado al portapapeles', 'success')
    }
  }

  onMount(loadUsers)
</script>

<div class="space-y-8">
  <div class="flex flex-col md:flex-row md:items-end justify-between gap-4">
    <div>
      <h2 class="text-4xl font-black text-white tracking-tight mb-2">Usuarios Whatsapp</h2>
      <p class="text-indigo-200/60 font-medium">Gestiona los clientes de Whatsapp</p>
    </div>
    <button 
      on:click={openCreateModal}
      class="group px-6 py-3 bg-gradient-to-r from-pink-500 via-purple-500 to-indigo-500 hover:from-pink-400 hover:via-purple-400 hover:to-indigo-400 text-white rounded-xl font-bold shadow-lg shadow-purple-500/30 transition-all duration-300 hover:scale-[1.02] active:scale-[0.98] relative overflow-hidden"
    >
      <div class="absolute inset-0 bg-white/20 group-hover:translate-x-full transition-transform duration-700 -skew-x-12 -translate-x-full"></div>
      <span class="relative z-10 flex items-center"><span class="mr-2 text-xl leading-none">+</span> Nuevo Cliente</span>
    </button>
  </div>

  <!-- Filtro de búsqueda -->
  <div class="bg-white/5 border border-white/10 rounded-3xl p-6 backdrop-blur-sm">
    <div class="flex gap-4 items-end">
      <div class="flex-1">
        <label class="block text-xs font-bold text-indigo-200 uppercase tracking-wider mb-2">Buscar Usuarios</label>
        <div class="relative">
          <input 
            bind:value={searchQuery}
            on:input={handleSearch}
            placeholder="Buscar por email, nombre o ID..."
            class="w-full px-4 py-3 pl-10 rounded-xl bg-black/30 border border-white/10 text-white focus:ring-2 focus:ring-pink-500/50 focus:border-pink-500/50 transition-all outline-none placeholder-white/20"
          />
          <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <svg class="h-5 w-5 text-indigo-200/30" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Users Table -->
  <div class="bg-white/5 border border-white/10 rounded-3xl overflow-hidden backdrop-blur-sm relative z-10">
    <div class="overflow-x-auto">
      <table class="w-full text-left border-collapse">
        <thead>
          <tr class="border-b border-white/10 bg-black/20">
            <th class="p-5 text-xs font-bold text-indigo-200 uppercase tracking-wider">Usuario / Email</th>
            <th class="p-5 text-xs font-bold text-indigo-200 uppercase tracking-wider">Tipo</th>
            <th class="p-5 text-xs font-bold text-indigo-200 uppercase tracking-wider">Estado</th>
            <th class="p-5 text-xs font-bold text-indigo-200 uppercase tracking-wider text-right">Acciones</th>
          </tr>
        </thead>
        <tbody class="divide-y divide-white/5">
          {#if loading}
            <tr>
              <td colspan="4" class="p-8 text-center text-indigo-200/50 animate-pulse">Cargando usuarios...</td>
            </tr>
          {:else if users.length === 0}
            <tr>
              <td colspan="4" class="p-8 text-center text-indigo-200/50">No hay usuarios registrados</td>
            </tr>
          {:else}
            {#each users as u}
              <tr class="hover:bg-white/5 transition-colors group">
                <td class="p-5">
                  <div class="font-bold text-white">{u.email}</div>
                  <div class="text-xs text-indigo-300/50 mt-1 font-mono">{u.id.slice(0, 8)}...</div>
                </td>
                <td class="p-5">
                  <span class="px-3 py-1 rounded-full text-xs font-bold border bg-blue-500/20 text-blue-300 border-blue-500/30">
                    CLIENT
                  </span>
                  {#if u.api_token_count !== undefined}
                    <div class="text-xs text-indigo-200/60 mt-1">
                      Tokens API: {u.api_token_count}
                    </div>
                  {/if}
                </td>
                <td class="p-5">
                  <button 
                    on:click={() => toggleActive(u)}
                    class="px-3 py-1 rounded-full text-xs font-bold border transition-all {u.active ? 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30 hover:bg-emerald-500/30' : 'bg-red-500/20 text-red-300 border-red-500/30 hover:bg-red-500/30'}"
                  >
                    {u.active ? 'ACTIVO' : 'INACTIVO'}
                  </button>
                </td>
                <td class="p-5 text-right space-x-2">
                  <button 
                    on:click={() => openAPITokenModal(u.id)}
                    disabled={apiTokenLoading}
                    class="px-3 py-1.5 rounded-lg text-xs font-medium bg-blue-500/20 text-blue-300 border border-blue-500/30 hover:bg-blue-500/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Ver/Crear Token API"
                  >
                    🔑 API Token
                  </button>
                  <button 
                    on:click={() => openEditModal(u)}
                    class="p-2 rounded-lg text-indigo-200/60 hover:text-white hover:bg-white/10 transition-colors"
                    title="Editar"
                  >
                    ✏️
                  </button>
                  <button 
                    on:click={() => confirmDelete(u.id)}
                    class="p-2 rounded-lg text-red-400/60 hover:text-red-300 hover:bg-red-500/10 transition-colors"
                    title="Eliminar"
                  >
                    🗑️
                  </button>
                </td>
              </tr>
            {/each}
          {/if}
        </tbody>
      </table>
    </div>
    
    <!-- Paginación -->
    {#if totalPages > 1}
      <div class="p-6 border-t border-white/10 flex items-center justify-between">
        <div class="text-sm text-indigo-200/60">
          Mostrando {(currentPage - 1) * pageSize + 1} - {Math.min(currentPage * pageSize, total)} de {total} usuarios
        </div>
        <div class="flex gap-2">
          <button
            on:click={() => goToPage(currentPage - 1)}
            disabled={currentPage === 1}
            class="px-4 py-2 rounded-lg bg-black/30 border border-white/10 text-white disabled:opacity-50 disabled:cursor-not-allowed hover:bg-white/10 transition-colors"
          >
            Anterior
          </button>
          
          {#each Array(Math.min(5, totalPages)) as _, i}
            {@const page = currentPage <= 3 ? i + 1 : currentPage >= totalPages - 2 ? totalPages - 4 + i : currentPage - 2 + i}
            {#if page >= 1 && page <= totalPages}
              <button
                on:click={() => goToPage(page)}
                class="px-4 py-2 rounded-lg border transition-colors {currentPage === page ? 'bg-pink-500/20 text-pink-300 border-pink-500/30' : 'bg-black/30 border-white/10 text-white hover:bg-white/10'}"
              >
                {page}
              </button>
            {/if}
          {/each}
          
          <button
            on:click={() => goToPage(currentPage + 1)}
            disabled={currentPage === totalPages}
            class="px-4 py-2 rounded-lg bg-black/30 border border-white/10 text-white disabled:opacity-50 disabled:cursor-not-allowed hover:bg-white/10 transition-colors"
          >
            Siguiente
          </button>
        </div>
      </div>
    {/if}
  </div>
</div>

<!-- Modals -->
<Modal 
  isOpen={showModal} 
  title={modalMode === 'create' ? 'Nuevo Cliente' : 'Editar Usuario'} 
  on:close={() => showModal = false}
>
  <UserForm 
    bind:formData 
    loading={formLoading} 
    mode={modalMode} 
    on:submit={handleSubmit} 
    on:cancel={() => showModal = false} 
  />
</Modal>

<ConfirmModal 
  isOpen={showConfirm}
  title="Eliminar Usuario"
  message="¿Estás seguro de que deseas eliminar este usuario? Esta acción no se puede deshacer y eliminará todas sus sesiones asociadas."
  confirmText="Sí, Eliminar"
  on:confirm={deleteUser}
  on:cancel={() => showConfirm = false}
/>

<!-- Modal de Token API -->
{#if showAPITokenModal}
  <div class="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4" transition:fade>
    <div class="bg-gray-900 border border-white/10 rounded-3xl shadow-2xl w-full max-w-md p-8 relative overflow-hidden" transition:slide>
      <div class="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-blue-500 via-purple-500 to-indigo-500"></div>
      
      <h3 class="text-2xl font-black text-white mb-6">
        {apiTokenData?.exists ? 'Token API Actual' : 'Token API Generado'}
      </h3>
      
      {#if apiTokenLoading}
        <div class="flex items-center justify-center py-8">
          <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
        </div>
      {:else if apiTokenData?.token}
        <div class="space-y-4">
          <div>
            <label class="block text-xs font-bold text-indigo-200 uppercase tracking-wider mb-2">
              Token API
            </label>
            <div class="flex gap-2">
              <input
                type="text"
                value={apiTokenData.token}
                readonly
                class="flex-1 px-4 py-3 rounded-xl bg-black/30 border border-white/10 text-white font-mono text-sm"
              />
              <button
                on:click={copyAPIToken}
                class="px-4 py-3 rounded-xl bg-blue-500/20 text-blue-300 border border-blue-500/30 hover:bg-blue-500/30 transition-colors"
                title="Copiar"
              >
                📋
              </button>
            </div>
            {#if apiTokenData.exists}
              <p class="mt-2 text-sm text-indigo-200/60 font-medium">
                Este es tu token API actual. Puedes regenerarlo si necesitas uno nuevo.
              </p>
            {:else}
              <p class="mt-2 text-sm text-indigo-200/60 font-medium">
                ⚠️ Guarda este token de forma segura. Solo se mostrará una vez.
              </p>
            {/if}
          </div>
          {#if apiTokenData.name}
            <div>
              <label class="block text-xs font-bold text-indigo-200 uppercase tracking-wider mb-2">
                Nombre
              </label>
              <div class="px-4 py-3 rounded-xl bg-black/30 border border-white/10 text-white text-sm">
                {apiTokenData.name}
              </div>
            </div>
          {/if}
          {#if apiTokenData.created_at}
            <div>
              <label class="block text-xs font-bold text-indigo-200 uppercase tracking-wider mb-2">
                Creado el
              </label>
              <div class="px-4 py-3 rounded-xl bg-black/30 border border-white/10 text-white text-sm">
                {new Date(apiTokenData.created_at).toLocaleString('es-ES')}
              </div>
            </div>
          {/if}
        </div>
      {:else}
        <div class="text-center py-8">
          <p class="text-indigo-200/60 mb-4">No se pudo obtener el token API.</p>
        </div>
      {/if}
      
      <div class="flex gap-3 mt-8">
        {#if apiTokenData?.exists}
          <button
            on:click={regenerateAPIToken}
            disabled={apiTokenLoading}
            class="flex-1 px-6 py-3 rounded-xl bg-yellow-500/20 text-yellow-300 border border-yellow-500/30 hover:bg-yellow-500/30 transition-colors font-bold disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {apiTokenLoading ? 'Regenerando...' : 'Regenerar Token'}
          </button>
        {/if}
        <button
          on:click={() => {
            showAPITokenModal = false
            apiTokenData = null
            currentUserIdForToken = ''
          }}
          class="flex-1 px-6 py-3 rounded-xl bg-black/30 border border-white/10 text-white hover:bg-white/10 transition-colors font-bold"
        >
          Cerrar
        </button>
      </div>
    </div>
  </div>
{/if}
